/**
 * Confetti cannon sound cues, at module scope so they can be UNLOCKED during the
 * unboxing Unwrap gesture and PLAYED later when the confetti fires.
 *
 * Why the split: on iOS, audio unlock is per-element. Playing the unboxing
 * <video> on the Unwrap tap unlocks only that video element — these separate
 * Audio() objects stay locked, so a later timer-driven `.play()` is blocked and
 * the confetti is silent on mobile. `primeConfettiAudio()` (called within the
 * gesture) does a silent play→pause to unlock them; `playConfettiCues()` then
 * plays on cue. Two cues, one per cannon: the poof leads (left), the softer pop
 * answers (right) a beat later.
 */
const POOF_SRC =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/EXPLDsgn-confetti_canon_poof-Elevenlabs.mp3?v=1783562431";
const POOF_VOLUME = 0.35;
const POP_SRC =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/Soft_popping_sound_o__1-1783733179936.mp3?v=1783733250";
const POP_VOLUME = 0.3;

let poof: HTMLAudioElement | null = null;
let pop: HTMLAudioElement | null = null;

function ensureLoaded(): void {
  if (typeof Audio === "undefined") return;
  if (!poof) {
    poof = new Audio(POOF_SRC);
    poof.preload = "auto";
    poof.volume = POOF_VOLUME;
    poof.load();
  }
  if (!pop) {
    pop = new Audio(POP_SRC);
    pop.preload = "auto";
    pop.volume = POP_VOLUME;
    pop.load();
  }
}

/**
 * Unlock the cues for later playback — MUST be called from within a user gesture
 * (the Unwrap tap). A muted play→pause primes each element so iOS allows the
 * later, timer-driven `.play()`. Idempotent; safe to call more than once.
 */
export function primeConfettiAudio(): void {
  ensureLoaded();
  for (const a of [poof, pop]) {
    if (!a) continue;
    const vol = a.volume;
    a.volume = 0;
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        a.pause();
        a.currentTime = 0;
        a.volume = vol;
      }).catch(() => {
        a.volume = vol; // never primed (no gesture / blocked) — restore volume
      });
    } else {
      try {
        a.pause();
        a.currentTime = 0;
      } catch {
        /* ignore */
      }
      a.volume = vol;
    }
  }
}

/** Play the two staggered cannon cues. `staggerMs` matches the visual cannons. */
export function playConfettiCues(staggerMs: number): void {
  ensureLoaded();
  const play = (a: HTMLAudioElement | null) => {
    if (!a) return;
    a.currentTime = 0;
    a.play().catch(() => {
      /* blocked (e.g. never unlocked) — fail silently */
    });
  };
  play(poof);
  window.setTimeout(() => play(pop), staggerMs);
}
